import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { InfoHint } from "@dolfin/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { ProblemDetailsAlert } from "@/components/ProblemDetailsAlert";
import { formatNumber } from "@/lib/format";
import type { ProblemDetails } from "@/lib/problemDetails";

type ApSettings = components["schemas"]["ApSettingsResponse"];
type PatchApSettings = components["schemas"]["PatchApSettingsRequest"];

interface ApSettingsSectionProps {
  clientId: string;
}

// Every ApSettingsResponse field belongs in SECTIONS (support reads this card); one entry gives both view and edit.
// Ranges aren't re-checked here: the backend validates and its message is shown verbatim.

type FieldKind =
  | "toggle"
  | "number"
  | "fraction"
  | "integer"
  | "days"
  | "slug"
  | "hosts";

type EditableKey = keyof ApSettings & keyof PatchApSettings;

interface FieldDef {
  key: EditableKey;
  label: string;
  kind: FieldKind;
  hint?: string;
  min?: number;
  max?: number;
  step?: number;
}

interface SectionDef {
  title: string;
  /** Hosts lists are wide, so their section uses two columns instead of three. */
  columns?: 2 | 3;
  fields: FieldDef[];
}

const ZERO_TO_ONE = { min: 0, max: 1, step: 0.01 };
const WINDOW_DAYS = { min: 0, max: 30, step: 1 };

const SECTIONS: SectionDef[] = [
  {
    title: "General",
    fields: [
      { key: "billReviewTimeAfterDueDate", label: "Bill review time after due date", kind: "days", min: 1, step: 1 },
      // Opt-out: Disabled explains "our approvers never got an email".
      { key: "apEmailsEnabled", label: "Payables emails", kind: "toggle" },
      { key: "payByBankEnabled", label: "Pay by bank", kind: "toggle" },
      { key: "gmailConnectorEnabled", label: "Gmail connector", kind: "toggle" },
      // Opt-out: Disabled explains duplicate bills not being caught.
      { key: "duplicateBillDetectionEnabled", label: "Duplicate bill detection", kind: "toggle" },
      {
        key: "billForwardingSlug",
        label: "Bill forwarding subdomain",
        kind: "slug",
        hint: "Cosmetic label in bills.<token>@<slug>.accountspayable.co. Bills route by each organisation's private token, never by this.",
      },
    ],
  },
  {
    title: "Allowed redirect hosts",
    columns: 2,
    fields: [
      {
        key: "gmailAllowedRedirectHosts",
        label: "Gmail",
        kind: "hosts",
        hint: "Bare hostnames the Gmail connector may return to after sign-in, one per line.",
      },
      {
        key: "paymentAllowedRedirectHosts",
        label: "Payment",
        kind: "hosts",
        hint: "Bare hostnames pay by bank may return to after bank authorisation, one per line.",
      },
    ],
  },
  {
    title: "Automatic matching",
    fields: [
      {
        key: "poAutoMatchEnabled",
        label: "Match against purchase orders",
        kind: "toggle",
        hint: "When off, nothing is allocated against a purchase order without a reviewer, including an identical SKU. Suggestions are still offered.",
      },
      {
        key: "dnAutoMatchEnabled",
        label: "Match against delivery notes",
        kind: "toggle",
        hint: "When off, nothing is allocated against a delivery note without a reviewer, including an identical SKU. Suggestions are still offered.",
      },
      {
        key: "matchAutoAcceptThreshold",
        label: "Auto-accept score",
        kind: "number",
        ...ZERO_TO_ONE,
        hint: "Score at or above which a fuzzy candidate is matched automatically. Every candidate reaching it is matched, up to the quantity billed. Never below the line similarity threshold.",
      },
    ],
  },
  {
    title: "Line matching",
    fields: [
      {
        key: "matchRoundingTolerance",
        label: "Rounding tolerance",
        kind: "number",
        min: 0,
        step: 0.01,
        hint: "Per-line amount difference treated as reconciled, in the bill's currency.",
      },
      {
        key: "matchSimilarityThreshold",
        label: "Similarity threshold",
        kind: "number",
        ...ZERO_TO_ONE,
        hint: "Minimum description similarity for a PO or delivery-note line to be suggested at all. Exact SKU matches bypass it.",
      },
      {
        key: "poMatchWindowDaysBefore",
        label: "PO window before (days)",
        kind: "integer",
        ...WINDOW_DAYS,
        hint: "Days an order's expected delivery date may fall before the invoice date and still score full marks on date. Past it the score tapers to nothing at 30 days.",
      },
      {
        key: "poMatchWindowDaysAfter",
        label: "PO window after (days)",
        kind: "integer",
        ...WINDOW_DAYS,
        hint: "Days an order's expected delivery date may fall after the invoice date and still score full marks on date.",
      },
      {
        key: "dnMatchWindowDaysBefore",
        label: "DN window before (days)",
        kind: "integer",
        ...WINDOW_DAYS,
        hint: "The same for a delivery note's received date. Before is usually the larger: goods are received before they are invoiced.",
      },
      {
        key: "dnMatchWindowDaysAfter",
        label: "DN window after (days)",
        kind: "integer",
        ...WINDOW_DAYS,
        hint: "Days a delivery note's received date may fall after the invoice date and still score full marks.",
      },
    ],
  },
  {
    title: "Variance tolerances",
    fields: [
      {
        key: "matchMaxPriceVarianceFraction",
        label: "Price",
        kind: "fraction",
        ...ZERO_TO_ONE,
        hint: "How far the billed unit price may differ from the ordered price, as a fraction (0.03 = 3%). Within it the gap neither lowers confidence nor needs a reviewer; beyond it, both.",
      },
      {
        key: "matchMaxReceiptQuantityVarianceFraction",
        label: "Delivery quantity",
        kind: "fraction",
        ...ZERO_TO_ONE,
        hint: "How far the billed quantity may differ from the quantity received, as a fraction. A short delivery never lowers confidence, but a shortfall past this band still waits for a reviewer.",
      },
      {
        key: "matchMaxOrderQuantityVarianceFraction",
        label: "Order quantity",
        kind: "fraction",
        ...ZERO_TO_ONE,
        hint: "How far the billed quantity may exceed the quantity ordered, as a fraction. Billing at or below the order is always clean.",
      },
    ],
  },
  {
    title: "Learning from manual matches",
    fields: [
      {
        key: "manualLinkSameItemBoost",
        label: "Same item boost",
        kind: "fraction",
        ...ZERO_TO_ONE,
        hint: "Uplift for lines repeating a description already confirmed for this bill line, on any document. This is what carries a split delivery. 0 switches it off.",
      },
      {
        key: "manualLinkSameDocumentBoost",
        label: "Same document boost",
        kind: "fraction",
        ...ZERO_TO_ONE,
        hint: "Uplift for lines on a document already linked elsewhere on this bill. Re-ranks suggestions only; never matches a line on its own. 0 switches it off.",
      },
    ],
  },
  {
    title: "Company bill routing",
    fields: [
      {
        key: "companyAutoAssignEnabled",
        label: "Auto-assign to organisation",
        kind: "toggle",
        hint: "Applies only to bills received against a company. When off, every one waits in the company inbox for someone to assign it.",
      },
      {
        key: "companyAutoAssignThreshold",
        label: "Routing score",
        kind: "number",
        ...ZERO_TO_ONE,
        hint: "How closely a bill's delivery address must match an organisation's before the bill is assigned to it. An exact postcode match scores 0.95, and a matching street adds up to 0.05 more.",
      },
      {
        key: "companyAutoAssignMargin",
        label: "Routing ambiguity margin",
        kind: "number",
        ...ZERO_TO_ONE,
        hint: "How far ahead the best-matching organisation must be over the runner-up. The street is worth at most 0.05, so two sites sharing a postcode can only auto-assign when this is below 0.05.",
      },
    ],
  },
  {
    title: "Supplier matching",
    fields: [
      {
        key: "supplierMatchSimilarityThreshold",
        label: "Similarity threshold",
        kind: "number",
        ...ZERO_TO_ONE,
        hint: "Minimum combined score to suggest a fuzzy supplier link. Exact tax-id, email or name matches bypass it and auto-link.",
      },
      {
        key: "supplierMatchNameWeight",
        label: "Name weight",
        kind: "number",
        min: 0,
        step: 0.05,
        hint: "Weight of name similarity in the combined score, normalised against the address weight, so the two need not sum to 1.",
      },
      {
        key: "supplierMatchAddressWeight",
        label: "Address weight",
        kind: "number",
        min: 0,
        step: 0.05,
        hint: "Weight of delivery-address similarity in the combined score, normalised against the name weight.",
      },
    ],
  },
];

const ALL_FIELDS = SECTIONS.flatMap((s) => s.fields);

function toNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const num = typeof value === "number" ? value : parseFloat(String(value));
  return Number.isNaN(num) ? undefined : num;
}

/** A 0-1 fraction read as a percentage, which is how the tolerances and boosts are talked about. */
function formatFraction(num: number): string {
  return `${formatNumber(num)} (${formatNumber(Math.round(num * 1000) / 10)}%)`;
}

function HostList({ hosts }: { hosts: string[] | undefined }) {
  if (!hosts || hosts.length === 0) {
    return <span className="text-muted-foreground">None</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {hosts.map((host) => (
        <Badge key={host} variant="secondary" className="font-mono text-xs">
          {host}
        </Badge>
      ))}
    </div>
  );
}

function FieldValue({ field, settings }: { field: FieldDef; settings: ApSettings }) {
  const value = settings[field.key];

  switch (field.kind) {
    case "toggle":
      return <>{value ? "Enabled" : "Disabled"}</>;
    case "hosts":
      return <HostList hosts={value as string[] | undefined} />;
    case "slug":
      return value ? (
        <>{String(value)}</>
      ) : (
        <span className="text-muted-foreground">default (slug of client name)</span>
      );
    default: {
      const num = toNumber(value);
      if (num === undefined) return <span className="text-muted-foreground">Not set</span>;
      if (field.kind === "fraction") return <>{formatFraction(num)}</>;
      if (field.kind === "days") return <>{`${num} day${num === 1 ? "" : "s"}`}</>;
      return <>{formatNumber(num)}</>;
    }
  }
}

function FieldLabel({ field, htmlFor }: { field: FieldDef; htmlFor?: string }) {
  const Tag = htmlFor ? "label" : "span";
  return (
    <span className="text-muted-foreground flex items-center gap-1">
      <Tag htmlFor={htmlFor}>{field.label}</Tag>
      {field.hint && <InfoHint label={field.label}>{field.hint}</InfoHint>}
    </span>
  );
}

/** Form state: numbers and host lists are held as the text being typed, so a half-typed "0." survives. */
type Draft = Record<EditableKey, string | boolean>;

function toDraft(settings: ApSettings): Draft {
  const draft = {} as Draft;
  for (const field of ALL_FIELDS) {
    const value = settings[field.key];
    if (field.kind === "toggle") draft[field.key] = Boolean(value);
    else if (field.kind === "hosts") draft[field.key] = ((value as string[] | undefined) ?? []).join("\n");
    else draft[field.key] = value === undefined || value === null ? "" : String(value);
  }
  return draft;
}

function parseHosts(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((h) => h.trim())
    .filter(Boolean);
}

/** Only changed fields: sending the whole form would overwrite edits made since this page loaded. */
function buildPatch(
  draft: Draft,
  saved: ApSettings,
): { body: PatchApSettings; changed: number } | { error: string } {
  const body: Record<string, unknown> = {};
  const original = toDraft(saved);

  for (const field of ALL_FIELDS) {
    const next = draft[field.key];
    if (field.kind === "hosts") {
      const nextHosts = parseHosts(String(next));
      if (nextHosts.join("\n") !== parseHosts(String(original[field.key])).join("\n")) {
        body[field.key] = nextHosts;
      }
      continue;
    }
    if (next === original[field.key]) continue;

    if (field.kind === "toggle") {
      body[field.key] = next;
    } else if (field.kind === "slug") {
      const slug = String(next).trim();
      // PATCH treats a missing value as "leave alone", so there is no way to send "back to the default".
      if (!slug) return { error: `${field.label} can't be cleared once set. Enter a new value instead.` };
      body[field.key] = slug;
    } else {
      const text = String(next).trim();
      const num = Number(text);
      if (text === "" || !Number.isFinite(num)) return { error: `${field.label} must be a number.` };
      // "0.9" retyped as "0.90" is not a change.
      if (num === toNumber(saved[field.key])) continue;
      if ((field.kind === "integer" || field.kind === "days") && !Number.isInteger(num)) {
        return { error: `${field.label} must be a whole number.` };
      }
      body[field.key] = num;
    }
  }

  return { body: body as PatchApSettings, changed: Object.keys(body).length };
}

function FieldInput({
  field,
  value,
  onChange,
  disabled,
}: {
  field: FieldDef;
  value: string | boolean;
  onChange: (value: string | boolean) => void;
  disabled: boolean;
}) {
  const id = `ap-setting-${field.key}`;

  if (field.kind === "toggle") {
    return (
      <div className="flex h-9 items-center gap-2">
        <Checkbox
          id={id}
          checked={value === true}
          onCheckedChange={(checked) => onChange(checked === true)}
          disabled={disabled}
        />
        <label htmlFor={id} className="text-sm">
          Enabled
        </label>
      </div>
    );
  }

  if (field.kind === "hosts") {
    return (
      <Textarea
        id={id}
        value={String(value)}
        onChange={(e) => onChange(e.target.value)}
        placeholder="portal.example.com"
        rows={3}
        className="font-mono text-xs"
        disabled={disabled}
      />
    );
  }

  if (field.kind === "slug") {
    return (
      <Input
        id={id}
        value={String(value)}
        onChange={(e) => onChange(e.target.value)}
        placeholder="default (slug of client name)"
        className="font-mono"
        disabled={disabled}
      />
    );
  }

  return (
    <Input
      id={id}
      type="number"
      inputMode="decimal"
      value={String(value)}
      onChange={(e) => onChange(e.target.value)}
      min={field.min}
      max={field.max}
      step={field.step}
      className="font-mono"
      disabled={disabled}
    />
  );
}

export function ApSettingsSection({ clientId }: ApSettingsSectionProps) {
  const [settings, setSettings] = useState<ApSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveProblem, setSaveProblem] = useState<ProblemDetails | null>(null);

  const editing = draft !== null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDraft(null);
    setSaveProblem(null);
    api
      .GET("/admin/clients/{clientId}/ap-settings", {
        params: { path: { clientId } },
      })
      .then(({ data, error: apiError }) => {
        if (cancelled) return;
        if (apiError) {
          setError("Failed to load AP settings.");
        } else {
          setSettings(data ?? null);
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner className="size-5" />
      </div>
    );
  }
  if (error) {
    return <p className="text-destructive">{error}</p>;
  }
  if (!settings) return null;

  const patch = draft ? buildPatch(draft, settings) : null;
  const changed = patch && "changed" in patch ? patch.changed : 0;
  const draftError = patch && "error" in patch ? patch.error : null;

  function startEditing() {
    if (!settings) return;
    setDraft(toDraft(settings));
    setSaveProblem(null);
  }

  function cancelEditing() {
    setDraft(null);
    setSaveProblem(null);
  }

  async function handleSave() {
    if (!patch || "error" in patch || patch.changed === 0) return;

    setSaving(true);
    setSaveProblem(null);
    const { data, error: apiError } = await api.PATCH("/admin/clients/{clientId}/ap-settings", {
      params: { path: { clientId } },
      body: patch.body,
    });
    setSaving(false);

    if (apiError || !data) {
      setSaveProblem(
        (apiError as ProblemDetails | undefined) ?? { title: "Failed to save AP settings." },
      );
      return;
    }

    setSettings(data);
    setDraft(null);
    toast.success(`Saved ${patch.changed} AP setting${patch.changed === 1 ? "" : "s"}.`);
  }

  const actions = editing ? (
    <div className="flex items-center gap-2">
      {changed > 0 && (
        <span className="text-muted-foreground text-xs">
          {changed} change{changed === 1 ? "" : "s"}
        </span>
      )}
      <Button variant="ghost" size="sm" onClick={cancelEditing} disabled={saving}>
        Cancel
      </Button>
      <Button size="sm" onClick={handleSave} disabled={saving || changed === 0 || draftError !== null}>
        {saving && <Spinner className="size-4" />}
        Save
      </Button>
    </div>
  ) : (
    <Button variant="outline" size="sm" onClick={startEditing}>
      Edit
    </Button>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>AP Settings</CardTitle>
        <CardAction>{actions}</CardAction>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-muted-foreground text-sm">
          Client-scoped accounts-payable settings, applied to every organisation under this client.
          Client admins edit these in their own settings; changes saved here take effect immediately
          for every organisation.
        </p>

        {draftError && <ProblemDetailsAlert problem={{ title: "Can't save yet", detail: draftError }} />}
        {saveProblem && <ProblemDetailsAlert problem={saveProblem} fallback="Failed to save AP settings." />}

        {SECTIONS.map((section) => (
          <div key={section.title}>
            <h3 className="text-muted-foreground mb-2 text-xs font-bold tracking-wider uppercase">
              {section.title}
            </h3>
            <dl
              className={
                section.columns === 2
                  ? "grid grid-cols-1 gap-3 text-sm sm:grid-cols-2"
                  : "grid grid-cols-1 gap-3 text-sm sm:grid-cols-3"
              }
            >
              {section.fields.map((field) => (
                <div key={field.key} className="space-y-1">
                  <dt>
                    <FieldLabel
                      field={field}
                      htmlFor={editing && field.kind !== "toggle" ? `ap-setting-${field.key}` : undefined}
                    />
                  </dt>
                  <dd className={draft ? undefined : "font-mono"}>
                    {draft ? (
                      <FieldInput
                        field={field}
                        value={draft[field.key]}
                        onChange={(value) => setDraft({ ...draft, [field.key]: value })}
                        disabled={saving}
                      />
                    ) : (
                      <FieldValue field={field} settings={settings} />
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}

        {editing && <div className="flex justify-end">{actions}</div>}
      </CardContent>
    </Card>
  );
}
